type Ctor<T> = new (...args: any[]) => T;

interface WithCEHooks {
  prototype: {
    connectedCallback?: () => void;
    disconnectedCallback?: () => void;
  };
}

/**
 * Form-associated mixin that works with Turbo and shadow DOM.
 */
export function withFormSubmitter<TBase extends Ctor<HTMLElement> & WithCEHooks>(
  Base: TBase
): TBase {
  class EButtonSubmitter extends Base {
    static readonly formAssociated = true;

    private readonly _internals!: ElementInternals;
    private _ldSubmitter?: HTMLButtonElement;
    private _attrsMO?: MutationObserver;
    private _lastForm: HTMLFormElement | null = null;
    private _onFormData?: (e: FormDataEvent) => void;

    constructor(...args: any[]) {
      super(...(args as any));
      this._internals = (this as any).attachInternals();

      this.addEventListener('click', () => this._activate());
      this.addEventListener('keydown', (e: KeyboardEvent) => {
        if ((e.key === 'Enter' || e.key === ' ') && !this._disabledLike()) {
          e.preventDefault();
          this._activate();
        }
      });
    }

    connectedCallback() {
      const superConnected = (Base.prototype as any).connectedCallback;
      if (typeof superConnected === 'function') superConnected.call(this);

      this._ensureSubmitter(); // initial

      // Observe type changes
      this._attrsMO = new MutationObserver(muts => {
        if (muts.some(m => m.attributeName === 'type')) {
          this._ensureSubmitter();
        }
        if (this._isSubmitType()) this._reflectToSubmitter();
      });
      this._attrsMO.observe(this, { attributes: true });
    }

    disconnectedCallback() {
      const superDisconnected = (Base.prototype as any).disconnectedCallback;
      if (typeof superDisconnected === 'function') superDisconnected.call(this);

      this._attrsMO?.disconnect();
      this._attrsMO = undefined;
      this._teardownSubmitter();
    }

    formAssociatedCallback(form: HTMLFormElement | null) {
      if (!this._isSubmitType()) return; // ignore non-submit buttons

      if (this._lastForm && this._onFormData) {
        this._lastForm.removeEventListener('formdata', this._onFormData);
        this._onFormData = undefined;
      }
      this._lastForm = form;

      if (!this._ldSubmitter) return;

      if (form) {
        if (!this._isInsideForm(this._ldSubmitter, form)) {
          if (!form.id) form.id = `f_${Math.random().toString(36).slice(2)}`;
          this._ldSubmitter.setAttribute('form', form.id);
        } else {
          this._ldSubmitter.removeAttribute('form');
        }

        this._onFormData = (ev: FormDataEvent) => {
          const submitter = (ev as any).submitter as HTMLElement | undefined;
          if (submitter !== this._ldSubmitter) return;
          const name = this.getAttribute('name');
          // append only if this element has a name
          if (name) ev.formData.append(name, this.getAttribute('value') ?? '');
        };
        form.addEventListener('formdata', this._onFormData);
      } else {
        this._ldSubmitter.removeAttribute('form');
      }
    }

    private _isSubmitType() {
      return (this.getAttribute('type') || '').toLowerCase() === 'submit';
    }
    private _isResetType() {
      return (this.getAttribute('type') || '').toLowerCase() === 'reset';
    }

    private _ensureSubmitter() {
      if (!this._isSubmitType()) {
        this._teardownSubmitter();
        return;
      }
      if (this._ldSubmitter) return;
      const b = document.createElement('button');
      b.type = 'submit';
      b.hidden = true;
      Object.assign(b.style, {
        position: 'absolute',
        width: '0',
        height: '0',
        overflow: 'hidden',
        clipPath: 'inset(50%)'
      });
      this.appendChild(b);
      this._ldSubmitter = b;
      this._reflectToSubmitter();
    }

    private _teardownSubmitter() {
      if (this._ldSubmitter) {
        this._ldSubmitter.remove();
        this._ldSubmitter = undefined;
      }
      if (this._lastForm && this._onFormData) {
        this._lastForm.removeEventListener('formdata', this._onFormData);
        this._onFormData = undefined;
      }
    }

    private _activate() {
      if (this._disabledLike()) return;
      const form = this._internals.form;
      if (!form) return;

      if (this._isResetType()) {
        form.reset();
        return;
      }
      if (!this._isSubmitType()) return;

      this._reflectToSubmitter();
      if (this._ldSubmitter) form.requestSubmit(this._ldSubmitter);
    }

    private _reflectToSubmitter() {
      if (!this._isSubmitType() || !this._ldSubmitter) return;
      for (const a of [
        'formaction',
        'formmethod',
        'formenctype',
        'formnovalidate',
        'formtarget',
        'name',
        'value',
        'disabled'
      ]) {
        const v = this.getAttribute(a);
        if (v !== null) this._ldSubmitter.setAttribute(a, v);
        else this._ldSubmitter.removeAttribute(a);
      }
      this._ldSubmitter.type = 'submit';
    }

    private _isInsideForm(node: Node, form: HTMLFormElement): boolean {
      let n: Node | null = node;
      while (n) {
        if (n === form) return true;
        const as = (n as any).assignedSlot as HTMLSlotElement | null;
        n = as ?? n.parentNode ?? ((n as any).host as Node | null) ?? null;
      }
      return false;
    }

    private _disabledLike() {
      return this.hasAttribute('disabled') || this.getAttribute('aria-disabled') === 'true';
    }
  }

  return EButtonSubmitter as unknown as TBase;
}
